import React, { useState, useEffect, useRef } from 'react';
import { GripVertical } from 'lucide-react';
import { adminApi } from '../api';
import type { AdminFlowModel } from '../types';
import { INPUT, STATUS } from '../../styles/tokens';

interface FlowModelListProps {
    models: AdminFlowModel[];
    loading: boolean;
    error: string;
    onRefresh: () => void;
    onCreate: () => void;
    onEdit: (item: AdminFlowModel) => void;
    onDelete: (id: string) => void;
    onReorder: (from: number, to: number) => void;
    onImport: (model: AdminFlowModel) => void;
}

// ── 匯出 Modal ─────────────────────────────────────────────────────────────────

const ExportModal: React.FC<{ model: AdminFlowModel; onClose: () => void; }> = ({ model, onClose }) => {
    const json = JSON.stringify(model, null, 2);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(json);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            textareaRef.current?.select();
            document.execCommand('copy');
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-[600px] max-w-[90vw] max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-4 border-b dark:border-gray-700">
                    <div>
                        <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">匯出工具包</h2>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{model.title}</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors text-lg leading-none">✕</button>
                </div>
                <div className="flex-1 overflow-hidden p-4">
                    <textarea
                        ref={textareaRef}
                        readOnly
                        value={json}
                        className="w-full h-full min-h-[300px] font-mono text-xs bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 border dark:border-gray-700 rounded-lg p-3 resize-none focus:outline-none"
                    />
                </div>
                <div className="flex justify-end gap-2 px-5 py-4 border-t dark:border-gray-700">
                    <button onClick={onClose} className="text-sm px-4 py-2 rounded-lg border dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                        關閉
                    </button>
                    <button onClick={handleCopy} className="text-sm px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors min-w-[80px]">
                        {copied ? '已複製 ✓' : '複製 JSON'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ── 匯入 Modal（2-step：JSON 貼上 → 圖片重新上傳）────────────────────────────

/** 圖片項目型別，追蹤每張圖片的上傳狀態 */
type ImageEntry = {
    key: string;
    label: string;
    originalUrl: string;
    currentUrl: string;
    status: 'idle' | 'uploading' | 'done' | 'error';
    error?: string;
};

/** 從 model 中收集所有圖片 URL（封面圖 + 選項縮圖） */
function collectImages(model: AdminFlowModel): ImageEntry[] {
    const result: ImageEntry[] = [];
    if (model.image) {
        result.push({ key: 'cover', label: '封面圖', originalUrl: model.image, currentUrl: model.image, status: 'idle' });
    }
    model.questions.forEach((q, qi) => {
        (q.options ?? []).forEach((opt, oi) => {
            if (opt.thumbnail) {
                result.push({
                    key: `q${qi}_o${oi}`,
                    label: `問題 ${qi + 1}「${q.title || '未命名'}」/ 選項「${opt.label || opt.value}」縮圖`,
                    originalUrl: opt.thumbnail,
                    currentUrl: opt.thumbnail,
                    status: 'idle',
                });
            }
        });
    });
    return result;
}

/** 將更新後的圖片 URL 套用回 model */
function applyImageUpdates(model: AdminFlowModel, entries: ImageEntry[]): AdminFlowModel {
    const urlMap = Object.fromEntries(entries.map(e => [e.originalUrl, e.currentUrl]));
    return {
        ...model,
        image: model.image ? (urlMap[model.image] ?? model.image) : model.image,
        questions: model.questions.map(q => ({
            ...q,
            options: (q.options ?? []).map(opt => ({
                ...opt,
                thumbnail: opt.thumbnail ? (urlMap[opt.thumbnail] ?? opt.thumbnail) : opt.thumbnail,
            })),
        })),
    };
}

const ImportModal: React.FC<{ onClose: () => void; onImport: (model: AdminFlowModel) => void; }> = ({ onClose, onImport }) => {
    // Step 1 狀態
    const [text, setText] = useState('');
    const [error, setError] = useState('');

    // Step 2 狀態
    const [step, setStep] = useState<1 | 2>(1);
    const [parsedModel, setParsedModel] = useState<AdminFlowModel | null>(null);
    const [imageEntries, setImageEntries] = useState<ImageEntry[]>([]);

    // ── Step 1：解析 JSON ──
    const handleNext = () => {
        setError('');
        let parsed: AdminFlowModel;
        try {
            parsed = JSON.parse(text.trim());
        } catch {
            setError('JSON 格式錯誤，請確認貼上的內容正確');
            return;
        }

        // 基本格式驗證
        if (!parsed.title || !Array.isArray(parsed.questions)) {
            setError('JSON 結構不符，需包含 title 與 questions 欄位');
            return;
        }

        setParsedModel(parsed);

        // 收集圖片，若無圖片則直接匯入
        const images = collectImages(parsed);
        if (images.length === 0) {
            onImport(parsed);
            onClose();
            return;
        }

        // 有圖片，進入 Step 2
        setImageEntries(images);
        setStep(2);
    };

    // ── Step 2：重新上傳單張圖片 ──
    const reuploadImage = async (key: string) => {
        setImageEntries(prev => prev.map(e => e.key === key ? { ...e, status: 'uploading', error: undefined } : e));

        const entry = imageEntries.find(e => e.key === key);
        if (!entry) return;

        try {
            // 下載原始圖片
            const response = await fetch(entry.originalUrl);
            if (!response.ok) throw new Error(`下載失敗 (HTTP ${response.status})`);
            const blob = await response.blob();

            // 從 URL 取得檔名
            const urlPath = new URL(entry.originalUrl).pathname;
            const filename = urlPath.split('/').pop() || 'image.png';
            const file = new File([blob], filename, { type: blob.type || 'image/png' });

            // 上傳到當前環境的 GCS
            const { url } = await adminApi.uploadToolkitImage(file);

            setImageEntries(prev => prev.map(e => e.key === key ? { ...e, status: 'done', currentUrl: url } : e));
        } catch (err: any) {
            const message = err.message?.includes('Failed to fetch')
                ? '無法下載圖片（可能是 CORS 限制）'
                : (err.message || '上傳失敗');
            setImageEntries(prev => prev.map(e => e.key === key ? { ...e, status: 'error', error: message } : e));
        }
    };

    // ── Step 2：一鍵重新上傳全部 ──
    const reuploadAll = () => {
        const pending = imageEntries.filter(e => e.status === 'idle' || e.status === 'error');
        pending.forEach(e => reuploadImage(e.key));
    };

    // ── Step 2：完成匯入（套用更新後的 URL） ──
    const handleFinish = () => {
        if (!parsedModel) return;
        const updated = applyImageUpdates(parsedModel, imageEntries);
        onImport(updated);
        onClose();
    };

    // ── Step 2：略過直接匯入（使用原始 URL） ──
    const handleSkip = () => {
        if (!parsedModel) return;
        onImport(parsedModel);
        onClose();
    };

    // 是否有正在上傳的圖片
    const isAnyUploading = imageEntries.some(e => e.status === 'uploading');

    // ── 渲染 ──
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-[640px] max-w-[90vw] max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
                {/* 標題列 */}
                <div className="flex items-center justify-between px-5 py-4 border-b dark:border-gray-700">
                    <div>
                        <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">匯入工具包</h2>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                            {step === 1 ? '貼上匯出的 JSON 內容' : '重新上傳圖片到目前環境'}
                        </p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors text-lg leading-none">✕</button>
                </div>

                {/* Step 1：貼上 JSON */}
                {step === 1 && (
                    <>
                        <div className="flex-1 overflow-hidden p-4">
                            <textarea
                                value={text}
                                onChange={e => { setText(e.target.value); setError(''); }}
                                placeholder='貼上 JSON 內容，例如：&#10;{&#10;  "title": "工具包名稱",&#10;  "questions": [...]&#10;}'
                                className="w-full h-full min-h-[300px] font-mono text-xs bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 border dark:border-gray-700 rounded-lg p-3 resize-none focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                        </div>
                        {error && (
                            <p className={`mx-4 mb-2 text-xs ${STATUS.error} px-3 py-2 rounded`}>{error}</p>
                        )}
                        <div className="flex justify-end gap-2 px-5 py-4 border-t dark:border-gray-700">
                            <button onClick={onClose} className="text-sm px-4 py-2 rounded-lg border dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                                取消
                            </button>
                            <button
                                onClick={handleNext}
                                disabled={!text.trim()}
                                className="text-sm px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[80px]"
                            >
                                下一步
                            </button>
                        </div>
                    </>
                )}

                {/* Step 2：圖片重新上傳 */}
                {step === 2 && (
                    <>
                        <div className="flex-1 overflow-auto p-4 space-y-3">
                            {/* 說明文字 */}
                            <div className={`text-xs ${STATUS.warning} px-3 py-2 rounded`}>
                                以下圖片來自其他環境，建議重新上傳到目前環境，避免圖片連結失效。
                            </div>

                            {/* 一鍵重新上傳 */}
                            <div className="flex justify-end">
                                <button
                                    onClick={reuploadAll}
                                    disabled={isAnyUploading || imageEntries.every(e => e.status === 'done')}
                                    className="text-xs px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    一鍵重新上傳全部
                                </button>
                            </div>

                            {/* 圖片列表 */}
                            {imageEntries.map(entry => (
                                <div key={entry.key} className="flex items-center gap-3 p-3 rounded-lg border dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                                    {/* 縮圖預覽 */}
                                    <div className="w-12 h-12 rounded overflow-hidden bg-gray-200 dark:bg-gray-700 flex-shrink-0">
                                        <img
                                            src={entry.currentUrl}
                                            alt={entry.label}
                                            className="w-full h-full object-cover"
                                            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                        />
                                    </div>

                                    {/* 標籤與狀態 */}
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-medium text-gray-700 dark:text-gray-200 truncate">{entry.label}</p>
                                        <p className="text-xs mt-0.5">
                                            {entry.status === 'idle' && <span className="text-gray-400 dark:text-gray-500">待上傳</span>}
                                            {entry.status === 'uploading' && <span className="text-blue-500">上傳中...</span>}
                                            {entry.status === 'done' && <span className="text-green-500">已完成</span>}
                                            {entry.status === 'error' && <span className="text-red-500">{entry.error || '失敗'}</span>}
                                        </p>
                                    </div>

                                    {/* 操作按鈕 */}
                                    <button
                                        onClick={() => reuploadImage(entry.key)}
                                        disabled={entry.status === 'uploading'}
                                        className="text-xs px-3 py-1.5 rounded border dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                                    >
                                        {entry.status === 'done' ? '重新上傳' : '上傳'}
                                    </button>
                                </div>
                            ))}
                        </div>

                        <div className="flex justify-between px-5 py-4 border-t dark:border-gray-700">
                            <button
                                onClick={handleSkip}
                                disabled={isAnyUploading}
                                className="text-sm px-4 py-2 rounded-lg border dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                            >
                                略過直接匯入
                            </button>
                            <button
                                onClick={handleFinish}
                                disabled={isAnyUploading}
                                className="text-sm px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[80px]"
                            >
                                完成匯入
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

// ── FlowModelList ──────────────────────────────────────────────────────────────

const FlowModelList: React.FC<FlowModelListProps> = ({ models, loading, error, onCreate, onEdit, onDelete, onReorder, onImport }) => {
    const [dragFrom, setDragFrom] = useState<number | null>(null);
    const [dragOver, setDragOver] = useState<number | null>(null);
    const [categories, setCategories] = useState<string[]>([]);
    const [newCatName, setNewCatName] = useState('');
    const [catError, setCatError] = useState('');
    const [exportModel, setExportModel] = useState<AdminFlowModel | null>(null);
    const [showImport, setShowImport] = useState(false);

    useEffect(() => {
        adminApi.getToolkitCategories().then(cats => setCategories(cats.map(c => c.name))).catch(() => { });
    }, []);

    const handleAddCategory = async () => {
        const name = newCatName.trim();
        if (!name) return;
        setCatError('');
        try {
            await adminApi.createToolkitCategory(name);
            setNewCatName('');
            const cats = await adminApi.getToolkitCategories();
            setCategories(cats.map(c => c.name));
        } catch (e: any) {
            setCatError(e.message);
        }
    };

    const handleDeleteCategory = async (name: string) => {
        if (!confirm(`確定要刪除分類「${name}」？`)) return;
        try {
            await adminApi.deleteToolkitCategory(name);
            setCategories(prev => prev.filter(c => c !== name));
        } catch (e: any) {
            alert(e.message);
        }
    };

    return (
        <>
            {exportModel && <ExportModal model={exportModel} onClose={() => setExportModel(null)} />}
            {showImport && <ImportModal onClose={() => setShowImport(false)} onImport={onImport} />}

            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <h1 className="text-xl font-bold text-gray-800 dark:text-gray-100">工具包管理</h1>
                    <div className="flex items-center gap-2">
                        <button onClick={() => setShowImport(true)}
                            className="text-sm border dark:border-gray-600 text-gray-600 dark:text-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium">
                            匯入工具包
                        </button>
                        <button onClick={onCreate}
                            className="text-sm bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium">
                            + 新增工具包
                        </button>
                    </div>
                </div>

                {/* Category management */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4">
                    <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">管理分類</h2>
                    <div className="flex flex-wrap gap-2 mb-3">
                        {categories.length === 0 && <span className="text-xs text-gray-400 dark:text-gray-500">尚無分類</span>}
                        {categories.map(cat => (
                            <span key={cat} className="flex items-center gap-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 px-2.5 py-1 rounded-full">
                                {cat}
                                <button onClick={() => handleDeleteCategory(cat)} className="text-gray-400 hover:text-red-500 transition-colors leading-none">✕</button>
                            </span>
                        ))}
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            value={newCatName}
                            onChange={e => setNewCatName(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleAddCategory()}
                            placeholder="新增分類名稱"
                            className={`text-xs rounded px-2 py-1.5 w-40 ${INPUT.base}`}
                        />
                        <button onClick={handleAddCategory}
                            className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 transition-colors">
                            新增
                        </button>
                        {catError && <span className="text-xs text-red-500">{catError}</span>}
                    </div>
                </div>

                {error && (
                    <p className={`text-sm ${STATUS.error} px-3 py-2 rounded`}>{error}</p>
                )}

                {loading ? (
                    <p className="text-sm text-gray-400 dark:text-gray-500 py-8 text-center">載入中...</p>
                ) : (
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
                                    <th className="w-8 px-2 py-3" />
                                    <th className="text-left px-4 py-3 font-medium">ID</th>
                                    <th className="text-left px-4 py-3 font-medium">標題</th>
                                    <th className="text-left px-4 py-3 font-medium">分類</th>
                                    <th className="text-center px-4 py-3 font-medium">題數</th>
                                    <th className="text-right px-4 py-3 font-medium">操作</th>
                                </tr>
                            </thead>
                            <tbody>
                                {models.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="px-4 py-8 text-center text-gray-400 dark:text-gray-500">
                                            尚無工具包資料
                                        </td>
                                    </tr>
                                ) : models.map((m, idx) => (
                                    <tr
                                        key={m.id}
                                        draggable
                                        onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; setDragFrom(idx); }}
                                        onDragOver={e => { e.preventDefault(); setDragOver(idx); }}
                                        onDrop={e => { e.preventDefault(); if (dragFrom !== null && dragFrom !== idx) onReorder(dragFrom, idx); setDragFrom(null); setDragOver(null); }}
                                        onDragEnd={() => { setDragFrom(null); setDragOver(null); }}
                                        className={`border-b dark:border-gray-700 last:border-0 transition-colors ${dragOver === idx && dragFrom !== idx
                                                ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700'
                                                : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'
                                            }`}
                                    >
                                        <td className="px-2 py-3 text-gray-300 dark:text-gray-600 cursor-grab active:cursor-grabbing">
                                            <GripVertical size={14} />
                                        </td>
                                        <td className="px-4 py-3 font-mono text-xs text-gray-600 dark:text-gray-300 max-w-[8rem] truncate">{m.id}</td>
                                        <td className="px-4 py-3 text-gray-800 dark:text-gray-100 font-medium">{m.title}</td>
                                        <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{m.category || '—'}</td>
                                        <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-300">{m.questions.length}</td>
                                        <td className="px-4 py-3 text-right space-x-3">
                                            <button onClick={() => onEdit(m)} className="text-xs text-blue-500 hover:text-blue-700 transition-colors">
                                                編輯
                                            </button>
                                            <button onClick={() => setExportModel(m)} className="text-xs text-emerald-500 hover:text-emerald-700 transition-colors">
                                                匯出
                                            </button>
                                            <button onClick={() => onDelete(m.id)} className="text-xs text-red-400 hover:text-red-600 transition-colors">
                                                刪除
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </>
    );
};

export default FlowModelList;
